import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Star,
  Diamond,
  Award,
  ChevronDown,
  Info
} from 'lucide-react';

interface LevelBenefits {
  services_discount: number;
  products_discount: number;
  free_appointment_every: number | null;
  birthday_appointment: boolean;
}

interface SettingsFormProps {
  settings: {
    enabled?: boolean;
    points: {
      per_visit: number;
      per_referral: number;
      per_amount: number;
      expiration_days: number;
    };
    diamonds: {
      per_visits: number;
      visits_required: number;
      per_amount: number;
    };
    levels: {
      silver: {
        diamonds_required: number;
        benefits: LevelBenefits;
      };
      gold: {
        diamonds_required: number;
        benefits: LevelBenefits;
      };
      diamond: {
        diamonds_required: number;
        benefits: LevelBenefits;
      };
      vip: {
        diamonds_required: number;
        benefits: LevelBenefits;
      };
    };
  };
  onSave: (settings: any) => Promise<void>;
}

const levelColors = {
  silver: { bg: 'bg-gray-100', text: 'text-gray-600', icon: 'text-gray-500' },
  gold: { bg: 'bg-amber-100', text: 'text-amber-600', icon: 'text-amber-500' },
  diamond: { bg: 'bg-blue-100', text: 'text-blue-600', icon: 'text-blue-500' },
  vip: { bg: 'bg-purple-100', text: 'text-purple-600', icon: 'text-purple-500' }
};

const levelNames = {
  silver: 'כסף',
  gold: 'זהב',
  diamond: 'יהלום',
  vip: 'VIP'
};

export function SettingsForm({ settings, onSave }: SettingsFormProps) {
  const [formData, setFormData] = useState({
    enabled: settings.enabled ?? true,
    points: settings.points,
    diamonds: {
      per_visits: settings.diamonds?.per_visits ?? settings.diamonds?.per_consecutive_visits ?? 1,
      visits_required: settings.diamonds?.visits_required ?? settings.diamonds?.consecutive_visits_required ?? 10,
      per_amount: settings.diamonds?.per_amount ?? 1
    },
    levels: settings.levels
  });
  const [saving, setSaving] = useState(false);
  const [expandedLevel, setExpandedLevel] = useState<string | null>(null);

  useEffect(() => {
    setFormData({
      enabled: settings.enabled ?? true,
      points: settings.points,
      diamonds: {
        per_visits: settings.diamonds?.per_visits ?? settings.diamonds?.per_consecutive_visits ?? 1,
        visits_required: settings.diamonds?.visits_required ?? settings.diamonds?.consecutive_visits_required ?? 10,
        per_amount: settings.diamonds?.per_amount ?? 1
      },
      levels: settings.levels
    });
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    try {
      setSaving(true);
      await onSave(formData);
    } catch (error) {

    } finally {
      setSaving(false);
    }
  };

  const renderLevelCard = (level: keyof typeof formData.levels) => {
    const isExpanded = expandedLevel === level;
    const colors = levelColors[level];
    const name = levelNames[level];
    const data = formData.levels[level];

    return (
      <div key={level} className="bg-white rounded-xl shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setExpandedLevel(isExpanded ? null : level)}
          className="w-full px-4 py-4 flex items-center justify-between active:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 ${colors.bg} rounded-lg`}>
              <Award className={`h-5 w-5 ${colors.icon}`} />
            </div>
            <div className="text-right">
              <div className={`font-semibold ${colors.text}`}>רמת {name}</div>
              <div className="text-xs text-gray-500">
                {level === 'silver' ? 'רמת התחלה - 0 יהלומים' : `דרוש ${data.diamonds_required} יהלומים`}
              </div>
            </div>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="h-5 w-5 text-gray-400" />
          </motion.div>
        </button>

        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-gray-100"
          >
            <div className="p-4 space-y-4">
              {level !== 'silver' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    כמות יהלומים נדרשת
                  </label>
                  <input
                    type="number"
                    value={data.diamonds_required}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      levels: {
                        ...prev.levels,
                        [level]: {
                          ...prev.levels[level],
                          diamonds_required: parseInt(e.target.value) || 0
                        }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>
              )}

              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">הטבות הרמה</span>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      אחוז הנחה על שירותים
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={data.benefits.services_discount}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          levels: {
                            ...prev.levels,
                            [level]: {
                              ...prev.levels[level],
                              benefits: {
                                ...prev.levels[level].benefits,
                                services_discount: parseInt(e.target.value) || 0
                              }
                            }
                          }
                        }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="0"
                        max="100"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      אחוז הנחה על מוצרים
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={data.benefits.products_discount}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          levels: {
                            ...prev.levels,
                            [level]: {
                              ...prev.levels[level],
                              benefits: {
                                ...prev.levels[level].benefits,
                                products_discount: parseInt(e.target.value) || 0
                              }
                            }
                          }
                        }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="0"
                        max="100"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      תור חינם כל X תורים (השאר ריק אם לא רלוונטי)
                    </label>
                    <input
                      type="number"
                      value={data.benefits.free_appointment_every || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        levels: {
                          ...prev.levels,
                          [level]: {
                            ...prev.levels[level],
                            benefits: {
                              ...prev.levels[level].benefits,
                              free_appointment_every: e.target.value ? parseInt(e.target.value) : null
                            }
                          }
                        }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="1"
                      placeholder="לדוגמה: 10"
                    />
                  </div>

                  <label className="flex items-center gap-2 py-2">
                    <input
                      type="checkbox"
                      checked={data.benefits.birthday_appointment}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        levels: {
                          ...prev.levels,
                          [level]: {
                            ...prev.levels[level],
                            benefits: {
                              ...prev.levels[level].benefits,
                              birthday_appointment: e.target.checked
                            }
                          }
                        }
                      }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">תור חינם ביום הולדת</span>
                  </label>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} id="loyalty-settings-form" className="min-h-screen bg-gray-50 pb-24">
      <div className="space-y-4 px-4 pt-4">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900">מערכת נאמנות</div>
              <div className="text-xs text-gray-500 mt-1">הפעל או כבה את כל מערכת הנאמנות</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={formData.enabled}
                onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {formData.enabled && (
          <>
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex gap-2">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <div className="font-medium mb-1">איך זה עובד?</div>
                  <div className="text-blue-800 space-y-1 text-xs">
                    <div>• <strong>נקודות</strong> - לקוחות מרוויחים על כל ביקור או סכום שהם מוציאים</div>
                    <div>• <strong>יהלומים</strong> - מתקבלים כל X ביקורים או אחרי הוצאה של סכום גבוה</div>
                    <div>• <strong>רמות</strong> - ככל שהלקוח צובר יותר יהלומים, הוא עולה לרמה גבוהה יותר ומקבל הטבות טובות יותר</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-500" />
                  <div>
                    <div className="font-semibold text-gray-900">נקודות</div>
                    <div className="text-xs text-gray-500">כמה נקודות לקוח מקבל?</div>
                  </div>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">
                    נקודות על כל ביקור
                  </label>
                  <input
                    type="number"
                    value={formData.points.per_visit}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      points: { ...prev.points, per_visit: parseInt(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                  <div className="text-xs text-gray-500 mt-1">לדוגמה: 10 נקודות לכל ביקור</div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-2">
                    נקודות על כל 100 ש"ח שהלקוח מוציא
                  </label>
                  <input
                    type="number"
                    value={formData.points.per_amount}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      points: { ...prev.points, per_amount: parseInt(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                  <div className="text-xs text-gray-500 mt-1">לדוגמה: 5 נקודות על כל 100 ש"ח</div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-2">
                    תוקף נקודות (בימים)
                  </label>
                  <input
                    type="number"
                    value={formData.points.expiration_days}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      points: { ...prev.points, expiration_days: parseInt(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                  <div className="text-xs text-gray-500 mt-1">לדוגמה: 365 ימים (שנה)</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Diamond className="h-5 w-5 text-blue-500" />
                  <div>
                    <div className="font-semibold text-gray-900">יהלומים</div>
                    <div className="text-xs text-gray-500">יהלומים קובעים את רמת הנאמנות</div>
                  </div>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">
                    תן יהלום כל כמה ביקורים?
                  </label>
                  <input
                    type="number"
                    value={formData.diamonds.visits_required}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      diamonds: { ...prev.diamonds, visits_required: parseInt(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                  />
                  <div className="text-xs text-gray-500 mt-1">לדוגמה: יהלום אחד כל 10 ביקורים</div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-2">
                    כמה יהלומים לתת כל פעם?
                  </label>
                  <input
                    type="number"
                    value={formData.diamonds.per_visits}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      diamonds: { ...prev.diamonds, per_visits: parseInt(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                  />
                  <div className="text-xs text-gray-500 mt-1">לדוגמה: 1 יהלום</div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-2">
                    יהלומים על כל 500 ש"ח שהלקוח מוציא
                  </label>
                  <input
                    type="number"
                    value={formData.diamonds.per_amount}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      diamonds: { ...prev.diamonds, per_amount: parseInt(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                  <div className="text-xs text-gray-500 mt-1">לדוגמה: 1 יהלום על כל 500 ש"ח</div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="px-1">
                <div className="font-semibold text-gray-900 mb-1">רמות נאמנות</div>
                <div className="text-xs text-gray-500">לחץ על כל רמה להגדרת ההטבות שלה</div>
              </div>
              {renderLevelCard('silver')}
              {renderLevelCard('gold')}
              {renderLevelCard('diamond')}
              {renderLevelCard('vip')}
            </div>
          </>
        )}
      </div>
    </form>
  );
}
